import API from '@/api/apiUrls'
import { useApi } from '@/api/useApi'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { Project } from '@/stores/type/project.type'

import { useProjectUserStore } from '@/stores/projectUser'
import { useDjangoAuthStore } from '@/stores/authDjango'
import { useAuthorizationStore } from '@/stores/authorization'
import { useBaseStore } from '@haskoning/vue-basestore'
import type { ProjectUserType } from '@/stores/type/projectUser.type'
import type { ProjectPermissionType } from '@/stores/type/authorization.type'

const { api } = useApi()

const baseUrl = API.CORE.PROJECTS.INDEX

export const useProjectStore = defineStore('project', () => {
  const baseStore = useBaseStore<Project>({ service: baseUrl, api })

  const servicePermissionProjects = ref<ProjectPermissionType[]>([])
  const filterFields = ref(['name', 'location', 'client', 'description'])

  const getCurrentProject = computed(() => {
    return baseStore.currentItem.value ?? null
  })

  async function getProjectById(projectId: number) {
    try {
      const response = await api.service(baseUrl).get(projectId)
      return response
    } catch (error) {
      console.error(`Failed to fetch project with ID ${projectId}:`, error)
      throw error
    }
  }

  const getChangePermission = computed(() => (id: number) => {
    if (!servicePermissionProjects.value.length) return false
    const permissions =
      servicePermissionProjects.value.find((item) => item.id === id)?.permissions ?? []
    return permissions.includes('change')
  })

  const getDeletePermission = computed(() => (id: number) => {
    if (!servicePermissionProjects.value.length) return false
    const permissions =
      servicePermissionProjects.value.find((item) => item.id === id)?.permissions ?? []
    return permissions.includes('delete')
  })

  const fieldsFiltered = computed(() => {
    return baseStore.fieldsList.value.filter((field) =>
      filterFields.value.includes(field.parameter)
    )
  })

  async function servicePermissionItems() {
    const projectUserStore = useProjectUserStore()
    const authStore = useDjangoAuthStore()
    const authorizationStore = useAuthorizationStore()

    const userId = authStore.user?.id
    await projectUserStore.fetchItems({ user: userId })
    const projectUserItems = projectUserStore.items || []

    const roleItems = projectUserItems.map((item: ProjectUserType) => {
      return {
        role: item.role,
        id: item.project
      }
    })
    servicePermissionProjects.value = await authorizationStore.getServicePermissionByRole(
      roleItems,
      ['change_project', 'delete_project']
    )
  }

  // Refresh the currently loaded project from the API and update the store
  async function refreshCurrentProject(projectId?: number) {
    try {
      const idToFetch = projectId ?? baseStore.currentItem.value?.id
      if (!idToFetch) return null
      await baseStore.fetchItem(idToFetch)
      return baseStore.currentItem.value
    } catch (error) {
      console.error('Failed to refresh current project', error)
      return null
    }
  }

  // Listen for a global event so other parts of the app can notify DB changes
  // Dispatch an event like: window.dispatchEvent(new CustomEvent('db-changed', { detail: { projectId: 123 } }))
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('db-changed', (ev: any) => {
      const detail = ev?.detail || {}
      // if the event carries a projectId explicitly, refresh that project
      if (detail.projectId) {
        void refreshCurrentProject(detail.projectId)
        return
      }

      // otherwise refresh currently loaded project
      if (baseStore.currentItem.value && baseStore.currentItem.value.id) {
        void refreshCurrentProject(baseStore.currentItem.value.id)
      }
    })
  }

  function resetAll() {
    baseStore.closeCreateUpdateDialog()
    baseStore.closeDeleteDialog()
    baseStore.reset()
  }

  return {
    ...baseStore,
    refreshCurrentProject,
    resetAll,
    getCurrentProject,
    getChangePermission,
    getDeletePermission,
    fieldsFiltered,
    servicePermissionItems,
    filterFields,
    getProjectById
  }
})
